


import { ArrowDown } from "lucide-react";
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../components/auth/AuthRequiredRoute/AuthRequiredRoute";
import Column from "../../components/ui/Stack/Column";
import HStack from "../../components/ui/Stack/HStack";
import { useFetchInstallmentReceipt } from "../../features/loans/hooks/useFetchInstallmentReceipt";
import { useLoanInfo } from "../../features/loans/hooks/useLoanInfo";
import { formatMoneyNumber, formatThaiDate } from "../../utils/util";
import HeaderComponent from "../Header/HeaderComponent";
import styles from "./InstallmentReceiptDetailPage.module.css";
import { useTranslation } from "react-i18next";
import { useIsDesktop } from "../../hooks/useIsDesktop";
const RECEIPT_FILE_NAME = "receipt";

const InstallmentReceiptDetailPage = () => {
  const navigate = useNavigate();
  const { loanId, installmentId } = useParams();
  const { t } = useTranslation("installment");
  const { userData } = useAuth();

  const isLineBrowser = /Line\/[0-9.]+/.test(navigator.userAgent);
  const isDesktop = useIsDesktop(); 

  const { loanInfo } = useLoanInfo({ loanId, userData });

  const installmentDetail = loanInfo?.installments.find(
    (installment) => installment.installment_id?.toString() === installmentId
  );

  const { data: receiptImageUrl } = useFetchInstallmentReceipt({
    loanId,
    installmentName: installmentDetail?.installment_name,
  });

  const installmentOrderNumber = loanInfo?.installments.findIndex(
    (installment) => installment.installment_id?.toString() === installmentId
  );

  async function handleDownloadReceipt() {
    // Check if running in LINE's LIFF browser
  const isLineBrowser = /Line/i.test(navigator.userAgent);

  if (isLineBrowser) {
    const pathAndQuery = window.location.pathname + window.location.search;
    const host = 'auunjai.com'; // Development host
    const baseUrl = `https://${host}${pathAndQuery}?openExternalBrowser=1`;

    // Detect platform (iOS or Android)
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    let externalUrl;

    if (isAndroid) {
      // Android: Use intent URL to open in Chrome
      externalUrl = `intent://${host}${pathAndQuery}#Intent;scheme=https;package=com.android.chrome;end`;
    } else if (isIOS) {
      // iOS: Show a prompt to the user to open in Safari
      const userConfirmed = window.confirm(
        'This page works better in Safari. Would you like to open it in Safari now?'
      );
      if (userConfirmed) {
        externalUrl = baseUrl;
      } else {
        // If user cancels, stay on the current page
        return;
      }
    } else {
      // Fallback for other platforms
      externalUrl = baseUrl;
    }
    

    // Perform the redirect
    window.location.href = externalUrl;
    return;
  }

    // Otherwise download normally
    const a = document.createElement("a");
    a.href = receiptImageUrl;
    a.download = `${RECEIPT_FILE_NAME}-${installmentDetail?.installment_name}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  if (!installmentDetail) return null;

  const installmentDate = formatThaiDate(
    new Date(installmentDetail?.installment_date).toISOString()
  );

  return (
    <div className={styles.pageBg}>
      <HeaderComponent
        title={t("title")}
        onBack={() => {
          navigate(`/my-loans/${loanId}/receipt`);
        }}
      />
      <div className={styles.content}>
        <div className={styles.summaryCard}>
          <div className={styles.loanNo}>
            {t("loanNumber")} {loanInfo?.loan_name}
          </div>
          <Column gap={5}>
            <HStack justify="space-between" fullWidth>
              <div className={styles.label}>{t("installmentNo")}</div>
              <div className={styles.value}>
                {installmentOrderNumber + 1}/{loanInfo?.installments.length}
              </div>
            </HStack>
            <HStack justify="space-between" fullWidth>
              <div className={styles.label}>{t("date")}</div>
              <div className={styles.value}>{installmentDate}</div>
            </HStack>
            <HStack justify="space-between" fullWidth>
              <div className={styles.label}>{t("paid")}</div>
              <div className={styles.value}>
                {formatMoneyNumber(installmentDetail?.total_amount)}
              </div>
            </HStack>
          </Column>
        </div>

        <div className={styles.warningBox}>
          <img
            src="/assets/img/ci/icon/ico-receipt.svg"
            alt="icon"
            className={styles.warningIcon}
          />
          <span>{t("warning")}</span>
        </div>

        {/* ✅ Don't show PDF if inside LINE browser */}
        {!isLineBrowser && isDesktop && (
          <div className={styles.receiptSection}>
            <embed
              src={receiptImageUrl}
              type="application/pdf"
              className={styles.receiptPdf}
            />
          </div>
        )}

        <button className={styles.downloadBtn} onClick={handleDownloadReceipt}>
          <span className={styles.downloadIcon}>
            <ArrowDown />
          </span>{" "}
          {t("download")}
        </button>

        <div className={styles.noteSection}>
          <div className={styles.noteTitle}>{t("noteTitle")}</div>
          <div className={styles.noteText}>{t("noteText")}</div>
        </div>
      </div>
    </div>
  );
};

export default InstallmentReceiptDetailPage;

